import { css, SerializedStyles } from '@emotion/react';
import {
  EventTypes,
  GameEvent,
  GameEventOfType,
  TriggeredCardInfo,
} from '@mythos/game/GameEvents';
import { GameOptions } from '@mythos/game/GameOptions';
import { GameViewModel } from '@mythos/game/GameViewModel';
import { Phase } from '@mythos/game/Phases';
import * as Probability from '@mythos/game/Probability';
import { Resource, Resources } from '@mythos/game/Resources';
import { CardWithID, TributeCardWithID } from '@mythos/game/Rules';
import {
  CounterDelta,
  isZeroCounterDelta,
  sumCounters,
} from '@mythos/game/Utility';
import invariant from 'invariant';
import { mapValues } from 'lodash';
import { MEDIA_QUERY_MOBILE } from 'mobile_ui/MStyles';
import React, { useMemo } from 'react';
import { CardNameView } from './CardNameView';
import { BasicCardRenderer } from './CardView';
import DieView from './DieView';
import { UserWithColor } from './GraphQLHelpers';
import { HoverCardRenderer } from './HoverCardStore';
import Symbols, {
  ResourceToSymbol,
  SymbolView,
  TokenToSymbol,
} from './Symbols';
import { MakeHighlightedAgeTributeRenderer } from './TributeCardView';
import { PlayerSummarySectionHighlightStyle } from './TutorialViews';

import { UIColorPalette } from './ColorPalette';

// Grouped event types - arrays are sequences grouped together, single types group consecutively
type GroupedEventType = EventTypes | readonly EventTypes[];
const GROUPED_EVENT_TYPES: GroupedEventType[] = [
  [EventTypes.ASSIGN_LEADER, EventTypes.GAIN_CARD], // leader assignment
  [EventTypes.PURCHASE_CARD, EventTypes.GAIN_CARD],
  EventTypes.GAIN_TRIBUTES,
  EventTypes.DEAL_CARD,
  EventTypes.DEAL_TRIBUTE,
  EventTypes.DISCARD_CARD,
  EventTypes.PRODUCTION,
  EventTypes.BID,
  EventTypes.END_GAME_EFFECTS,
];

// Tagged union for event groups
type EventGroupBase = {
  age: number;
  turn: number;
  phase: Phase;
  key: string;
};

type SingleEventGroup<T extends EventTypes> = EventGroupBase & {
  kind: 'single';
  type: T;
  events: GameEventOfType<T>[];
};

type SequenceEventGroup = EventGroupBase & {
  kind: 'sequence';
  types: readonly EventTypes[];
  events: GameEvent[];
};

type EventGroup = SingleEventGroup<EventTypes> | SequenceEventGroup;

type RenderContext = {
  userByID: Map<string, UserWithColor>;
  cardByID: { [k: string]: CardWithID };
  tributeCardByID: { [k: string]: TributeCardWithID };
  options: GameOptions;
  age: number;
};

type EventRenderer =
  | {
      symbol: Symbols;
      symbolStyle?: React.CSSProperties;
      borderColor: string;
      bodyRenderer: (eg: EventGroup, context: RenderContext) => React.ReactNode;
    }
  | {
      itemRenderer: (eg: EventGroup) => React.ReactElement | null;
    };
const EventTypeToRenderer: Record<string, EventRenderer> = {
  [EventTypes.SPAWN_CARDS]: { itemRenderer: () => null },
  [EventTypes.SPAWN_TRIBUTES]: { itemRenderer: () => null },
  [EventTypes.DEBUG_SET_PLAYER_COUNTERS]: { itemRenderer: () => null },
  [EventTypes.SET_UP_AGE]: { itemRenderer: () => null },
  [EventTypes.ASSIGN_LEADER]: { itemRenderer: () => null }, // handled with GAIN_CARD in sequence
  // ASSIGN_LEADER + GAIN_CARD sequence (leader assignment)
  [[EventTypes.ASSIGN_LEADER, EventTypes.GAIN_CARD].join(',')]: {
    symbol: Symbols.CARD_AFFINITY_LEADER,
    borderColor: UIColorPalette.logBorderGold,
    bodyRenderer: (eg: EventGroup, { userByID, cardByID }) => {
      // Group events by cardID to pair ASSIGN_LEADER with GAIN_CARD
      const assignByCardID = new Map<
        string,
        GameEventOfType<EventTypes.ASSIGN_LEADER>
      >();
      const gainByCardID = new Map<
        string,
        GameEventOfType<EventTypes.GAIN_CARD>
      >();

      for (const event of eg.events) {
        if (event.type === EventTypes.ASSIGN_LEADER) {
          assignByCardID.set(event.payload.cardID, event);
        } else if (event.type === EventTypes.GAIN_CARD) {
          gainByCardID.set(event.payload.cardID, event);
        }
      }

      // Render each leader assignment
      return Array.from(assignByCardID.entries()).map(
        ([cardID, assignEvent], i) => {
          const gainEvent = gainByCardID.get(cardID);
          const user = userByID.get(assignEvent.payload.userID)!;
          const card = cardByID[cardID];

          return (
            <div key={i} css={ChatLogItemStyles.entry}>
              <UserName user={user} /> was assigned{' '}
              <CardNameView card={card} renderer={BasicCardRenderer} /> as their
              leader
              {gainEvent?.payload.cardCounters &&
              !isZeroCounterDelta(gainEvent.payload.cardCounters) ? (
                <>
                  {' '}
                  and gained{' '}
                  {renderCounterDelta(gainEvent.payload.cardCounters)}
                </>
              ) : null}
            </div>
          );
        },
      );
    },
  },
  [EventTypes.SETUP_TRIBUTE_DECK]: { itemRenderer: () => null },
  [EventTypes.SETUP_DECK]: { itemRenderer: () => null },
  [EventTypes.SETUP_BASIC_PILE]: { itemRenderer: () => null },
  [EventTypes.TRIBUTE_PLANNING_DATA]: { itemRenderer: () => null },
  [EventTypes.TRIBUTE_RESOLUTION_DATA]: { itemRenderer: () => null },
  [EventTypes.PURCHASE_CARD]: { itemRenderer: () => null },
  [EventTypes.END_OF_AGE]: { itemRenderer: () => null },

  // TODO(ldg): render this one
  [EventTypes.END_OF_GAME]: { itemRenderer: () => null },

  [EventTypes.SET_UP_TURN]: {
    itemRenderer: (eg) => {
      invariant(eg.events[0].type === EventTypes.SET_UP_TURN, 'ts');
      const turn = eg.events[0].payload.turn;
      return (
        <ChatLogDivider
          text={`Turn ${turn}`}
          style={
            (turn === 1 && {
              backgroundColor: UIColorPalette.logTurnOneDividerBackground,
            }) ||
            undefined
          }
        />
      );
    },
  },
  [EventTypes.SET_UP_PHASE]: { itemRenderer: () => null },
  [EventTypes.END_OF_PHASE]: { itemRenderer: () => null },
  [EventTypes.BID]: {
    symbol: Symbols.M3_DRAFT,
    borderColor: UIColorPalette.logBorderWar,
    bodyRenderer: (eg: EventGroup, { userByID, cardByID }) => {
      const events = eg.events as GameEventOfType<EventTypes.BID>[];
      return events.map((e, i) => {
        const user = userByID.get(e.payload.userID)!;
        const card = cardByID[e.payload.cardID];
        return (
          <div key={i} css={ChatLogItemStyles.entry}>
            <UserName user={user} /> bid{' '}
            {renderCounterDelta(
              { military: e.payload.bid.military },
              'military',
            )}{' '}
            on <CardNameView card={card} renderer={BasicCardRenderer} />
          </div>
        );
      });
    },
  },
  [EventTypes.CONFLICT_RESULTS]: {
    symbol: Symbols.MILITARY,
    borderColor: UIColorPalette.logBorderWar,
    bodyRenderer(eg: EventGroup, { userByID, cardByID }) {
      const event = eg
        .events[0] as GameEventOfType<EventTypes.CONFLICT_RESULTS>;
      const firstRound = event.payload.rounds[0];
      if (Object.keys(firstRound).length === 1) {
        return null;
      }
      return (
        <ConflictResultsView
          event={event}
          age={eg.age}
          userByID={userByID}
          cardByID={cardByID}
        />
      );
    },
  },
  [EventTypes.EARN_TOKEN]: {
    symbol: Symbols.ACQUIRE_TOKEN_WAR,
    borderColor: UIColorPalette.logBorderWar,
    bodyRenderer: (eg: EventGroup, { userByID, cardByID }) => {
      const events = eg.events as GameEventOfType<EventTypes.EARN_TOKEN>[];
      return events.map((e, i) => {
        const user = userByID.get(e.payload.userID)!;
        const token = e.payload.token;
        return (
          <div key={i} css={ChatLogItemStyles.entry}>
            <UserName user={user} />
            gained{' '}
            <SymbolView
              symbol={TokenToSymbol[token]}
              css={CounterDeltaStyles.symbol}
            />
            <TriggeredCardsView
              triggeredCards={e.payload.triggeredCards}
              cardByID={cardByID}
            />
          </div>
        );
      });
    },
  },
  [EventTypes.REFUND_MILITARY]: {
    symbol: Symbols.MILITARY,
    borderColor: UIColorPalette.logBorderWar,
    bodyRenderer: (eg: EventGroup, { userByID }) => {
      const events = eg.events as GameEventOfType<EventTypes.REFUND_MILITARY>[];
      return events.map((e, i) => {
        const user = userByID.get(e.payload.userID)!;
        return (
          <div key={i} css={ChatLogItemStyles.entry}>
            <UserName user={user} /> refunded{' '}
            {renderCounterDelta(
              { military: e.payload.refundedMilitary },
              'military',
            )}{' '}
            from a Clash.
          </div>
        );
      });
    },
  },
  // Combined PURCHASE_CARD + GAIN_CARD renderer
  [[EventTypes.PURCHASE_CARD, EventTypes.GAIN_CARD].join(',')]: {
    symbol: Symbols.M3_ACQUIRE,
    borderColor: UIColorPalette.logBorderGold,
    bodyRenderer: (eg: EventGroup, { userByID, cardByID }) => {
      // Group events by cardID to pair PURCHASE_CARD with GAIN_CARD
      const purchaseByCardID = new Map<
        string,
        GameEventOfType<EventTypes.PURCHASE_CARD>
      >();
      const gainByCardID = new Map<
        string,
        GameEventOfType<EventTypes.GAIN_CARD>
      >();

      for (const event of eg.events) {
        if (event.type === EventTypes.PURCHASE_CARD) {
          purchaseByCardID.set(event.payload.cardID, event);
        } else if (event.type === EventTypes.GAIN_CARD) {
          gainByCardID.set(event.payload.cardID, event);
        }
      }

      for (const cardID of purchaseByCardID.keys()) {
        invariant(
          gainByCardID.has(cardID),
          `Missing GAIN_CARD for PURCHASE_CARD ${cardID}`,
        );
      }
      invariant(
        purchaseByCardID.size === gainByCardID.size,
        'Mismatched event counts',
      );

      // Render each card acquisition
      return Array.from(gainByCardID.entries()).map(
        ([cardID, gainEvent], i) => {
          const purchaseEvent = purchaseByCardID.get(cardID)!;
          const user = userByID.get(gainEvent.payload.userID)!;
          const goldCost = purchaseEvent.payload.spentCounters?.gold || 0;

          return (
            <div key={i} css={ChatLogItemStyles.entry}>
              <UserName user={user} /> obtained{' '}
              <CardNameView
                card={cardByID[cardID]}
                renderer={BasicCardRenderer}
              />
              {goldCost > 0 ? (
                <> for {renderCounterDelta({ gold: goldCost }, 'gold')}</>
              ) : null}
              {gainEvent.payload.cardCounters &&
              !isZeroCounterDelta(gainEvent.payload.cardCounters) ? (
                <>
                  {' '}
                  and gained{' '}
                  {renderCounterDelta(gainEvent.payload.cardCounters)}
                </>
              ) : null}
              <TriggeredCardsView
                triggeredCards={gainEvent.payload.triggeredCards}
                cardByID={cardByID}
              />
            </div>
          );
        },
      );
    },
  },
  // Keep single GAIN_CARD renderer for backwards compatibility with old events
  [EventTypes.GAIN_CARD]: {
    symbol: Symbols.M3_ACQUIRE,
    borderColor: UIColorPalette.logBorderGold,
    bodyRenderer: (eg: EventGroup, { userByID, cardByID }) => {
      const events = eg.events as GameEventOfType<EventTypes.GAIN_CARD>[];
      return events.map((e, i) => {
        const user = userByID.get(e.payload.userID)!;
        return (
          <div key={i} css={ChatLogItemStyles.entry}>
            <UserName user={user} /> gained{' '}
            <CardNameView
              card={cardByID[e.payload.cardID]}
              renderer={BasicCardRenderer}
            />
            {e.payload.cardCounters &&
            !isZeroCounterDelta(e.payload.cardCounters) ? (
              <> for {renderCounterDelta(e.payload.cardCounters)}</>
            ) : null}
            <TriggeredCardsView
              triggeredCards={e.payload.triggeredCards}
              cardByID={cardByID}
            />
          </div>
        );
      });
    },
  },
  [EventTypes.GAIN_TRIBUTES]: {
    symbol: Symbols.TOKEN_TRIBUTE,
    symbolStyle: {
      filter:
        'brightness(0) saturate(100%) invert(46%) sepia(74%) saturate(405%) hue-rotate(219deg) brightness(92%) contrast(83%)',
    },
    borderColor: UIColorPalette.logBorderFavor,
    bodyRenderer: (eg: EventGroup, { userByID, cardByID, tributeCardByID }) => {
      const events = eg.events as GameEventOfType<EventTypes.GAIN_TRIBUTES>[];
      const renderer = MakeHighlightedAgeTributeRenderer(eg.age);
      return events.map((e, i) => {
        const user = userByID.get(e.payload.userID)!;
        return (
          <div key={i} css={ChatLogItemStyles.entry}>
            <UserName user={user} /> gained{' '}
            {renderCounterDelta({ favor: e.payload.favor }, 'favor')} from
            tributes:{' '}
            <CardsNameView
              cards={e.payload.tributeIDs.map((id) => tributeCardByID[id])}
              renderer={renderer}
            />
            <TriggeredCardsView
              triggeredCards={e.payload.triggeredCards}
              cardByID={cardByID}
            />
          </div>
        );
      });
    },
  },
  [EventTypes.PRODUCTION]: {
    symbol: Symbols.EFFECT_PRODUCTION,
    borderColor: UIColorPalette.logBorderProduction,
    bodyRenderer: (eg: EventGroup, { userByID }) => {
      const events = eg.events as GameEventOfType<EventTypes.PRODUCTION>[];
      return events.map((e, i) => {
        const user = userByID.get(e.payload.userID)!;
        return (
          <div key={i} css={ChatLogItemStyles.entry}>
            <UserName user={user} /> produced{' '}
            {renderCounterDelta(e.payload.counters)}
          </div>
        );
      });
    },
  },
  [EventTypes.END_GAME_EFFECTS]: {
    symbol: Symbols.FAVOR,
    borderColor: UIColorPalette.logBorderFavor,
    bodyRenderer: (eg: EventGroup, { userByID, cardByID }) => {
      const events =
        eg.events as GameEventOfType<EventTypes.END_GAME_EFFECTS>[];
      return (
        <>
          {events.map((e, i) => {
            const user = userByID.get(e.payload.userID)!;
            if (e.payload.triggeredCards.length === 0) return;
            const totalDelta = sumCounters(
              e.payload.triggeredCards.map((card) => card.counters),
            );
            return (
              <div key={i} css={ChatLogItemStyles.entry}>
                <UserName user={user} /> gained{' '}
                {renderCounterDelta(totalDelta, 'favor')} from{' '}
                <CardsNameView
                  cards={e.payload.triggeredCards.map(
                    (card) => cardByID[card.cardID],
                  )}
                  renderer={BasicCardRenderer}
                />
              </div>
            );
          })}
        </>
      );
    },
  },
  [EventTypes.TRIBUTES_RESHUFFLED]: {
    symbol: Symbols.CARD_BACK,
    borderColor: UIColorPalette.logBorderNeutral,
    bodyRenderer: () => 'Tributes reshuffled.',
  },
  [EventTypes.DEAL_CARD]: {
    symbol: Symbols.CARD_BACK,
    borderColor: UIColorPalette.logBorderNeutral,
    bodyRenderer: (eg: EventGroup, { cardByID }) => {
      const events = eg.events as GameEventOfType<EventTypes.DEAL_CARD>[];
      const cards = events.map((event) => {
        return cardByID[event.payload.cardID!];
      });
      return (
        <>
          Dealt <CardsNameView cards={cards} renderer={BasicCardRenderer} /> to
          the trade row
        </>
      );
    },
  },
  [EventTypes.DEAL_TRIBUTE]: {
    symbol: Symbols.CARD_BACK,
    borderColor: UIColorPalette.logBorderNeutral,
    bodyRenderer: (eg: EventGroup, { tributeCardByID }) => {
      const events = eg.events as GameEventOfType<EventTypes.DEAL_TRIBUTE>[];
      const renderer = MakeHighlightedAgeTributeRenderer(eg.age);
      const cards = events.map((event) => {
        return tributeCardByID[event.payload.cardID!];
      });
      return (
        <>
          Dealt <CardsNameView cards={cards} renderer={renderer} /> to the
          tribute row
        </>
      );
    },
  },
  [EventTypes.DISCARD_TRIBUTE]: { itemRenderer: () => null },
  [EventTypes.DISCARD_CARD]: {
    symbol: Symbols.CARD_BACK,
    borderColor: UIColorPalette.logBorderNeutral,
    bodyRenderer: (eg: EventGroup, { cardByID }) => {
      const events = eg.events as GameEventOfType<EventTypes.DISCARD_CARD>[];
      const cards = events.map((event) => {
        return cardByID[event.payload.cardID!];
      });
      return (
        <>
          Discarded <CardsNameView cards={cards} renderer={BasicCardRenderer} />{' '}
          from the trade row
        </>
      );
    },
  },
};
interface Props {
  game: GameViewModel;
  userByID: Map<string, UserWithColor>;

  highlightedEntryKey?: string;
}
export const LogView2 = (props: Props) => {
  const { game, userByID, highlightedEntryKey } = props;

  const eventGroups = useMemo(() => {
    const eventGroups: EventGroup[] = [];
    let group: EventGroup | null = null;
    let age = 0;
    let turn = 0;
    let phase: Phase = Phase.SETUP;

    game.events.forEach((e) => {
      if (e.type === EventTypes.SET_UP_AGE) {
        age = e.payload.age;
      } else if (e.type === EventTypes.SET_UP_TURN) {
        turn = e.payload.turn;
      } else if (e.type === EventTypes.SET_UP_PHASE) {
        phase = e.payload.phase;
      }

      const groupedType = findGroupedEventType(e.type);

      if (group && groupedType && canGroup(group, e.type)) {
        // Add to existing group
        (group.events as GameEvent[]).push(e);
      } else {
        // Start a new group
        if (group) {
          eventGroups.push(group);
        }
        if (Array.isArray(groupedType)) {
          group = {
            kind: 'sequence',
            types: groupedType,
            age,
            turn,
            phase,
            key: `${eventGroups.length}`,
            events: [e],
          };
        } else {
          group = {
            kind: 'single',
            type: groupedType || e.type,
            age,
            turn,
            phase,
            key: `${eventGroups.length}`,
            events: [e],
          } as SingleEventGroup<EventTypes>;
        }
      }
    });
    if (group) {
      eventGroups.push(group);
    }
    eventGroups.reverse();
    return eventGroups;
  }, [props.game.events]);
  return (
    <div css={LogStyles.container}>
      <div css={LogStyles.itemsContainer}>
        {eventGroups.map((eg) => {
          return (
            <EventGroupView
              key={eg.key}
              eg={eg}
              age={eg.age}
              userByID={userByID}
              cardByID={game.cardsByID}
              tributeCardByID={game.tributeCardsByID}
              options={game.options}
              containerStyles={
                highlightedEntryKey === eg.key
                  ? PlayerSummarySectionHighlightStyle
                  : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
};

function findGroupedEventType(eventType: EventTypes): GroupedEventType | null {
  for (const groupedType of GROUPED_EVENT_TYPES) {
    if (Array.isArray(groupedType)) {
      if (groupedType.includes(eventType)) {
        return groupedType;
      }
    } else if (groupedType === eventType) {
      return groupedType;
    }
  }
  return null;
}

function canGroup(group: EventGroup, eventType: EventTypes): boolean {
  if (group.kind === 'sequence') {
    return group.types.includes(eventType);
  }
  return group.type === eventType;
}

function eventGroupKey(group: EventGroup): string {
  return group.kind === 'sequence' ? group.types.join(',') : group.type;
}

const LogStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flexGrow: 1,

    [MEDIA_QUERY_MOBILE]: {
      flexGrow: 0,
    },
  }),
  itemsContainer: css({
    overflowY: 'auto',
    backgroundColor: UIColorPalette.logBG,
  }),
};

const EventGroupView = React.memo(
  (props: {
    eg: EventGroup;
    age: number;
    userByID: Map<string, UserWithColor>;
    cardByID: { [k: string]: CardWithID };
    tributeCardByID: { [k: string]: TributeCardWithID };
    options: GameOptions;
    containerStyles?: SerializedStyles;
  }) => {
    const {
      eg,
      age,
      userByID,
      cardByID,
      tributeCardByID,
      options,
      containerStyles,
    } = props;
    const rendererKey = eventGroupKey(eg);
    const renderer = EventTypeToRenderer[rendererKey];
    if (!renderer) {
      return null;
    }
    if ('bodyRenderer' in renderer) {
      const body = renderer.bodyRenderer(eg, {
        userByID,
        cardByID,
        tributeCardByID,
        options,
        age,
      });
      if (!body) {
        return null;
      }
      return (
        <ChatLogItem
          symbol={renderer.symbol}
          symbolStyle={renderer.symbolStyle}
          style={{ borderColor: renderer.borderColor }}
          css={containerStyles}
        >
          {body}
        </ChatLogItem>
      );
    } else {
      return renderer.itemRenderer(eg);
    }
  },
);

function ChatLogDivider(props: {
  text: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  const { text } = props;
  return (
    <div
      css={[ChatLogItemStyles.container, ChatLogItemStyles.divider]}
      className={props.className}
      style={props.style}
    >
      <div css={ChatLogItemStyles.dividerContent}>{text}</div>
    </div>
  );
}

function ChatLogItem(props: {
  className?: string;
  style?: React.CSSProperties;

  symbol: Symbols;
  symbolStyle?: React.CSSProperties;

  text?: React.ReactNode;
  body?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const { text, body, children } = props;
  return (
    <div
      css={ChatLogItemStyles.container}
      className={props.className}
      style={props.style}
    >
      <div css={ChatLogItemStyles.symbolContainer}>
        <SymbolView
          symbol={props.symbol}
          css={ChatLogItemStyles.symbol}
          style={props.symbolStyle}
        />
      </div>
      <div css={ChatLogItemStyles.contentContainer}>
        {text && <div css={ChatLogItemStyles.contentItemWrapper}>{text}</div>}
        {body && <div css={ChatLogItemStyles.contentItemWrapper}>{body}</div>}
        {children && (
          <div css={ChatLogItemStyles.contentItemWrapper}>{children}</div>
        )}
      </div>
    </div>
  );
}
const ChatLogItemStyles = {
  container: css({
    display: 'flex',
    padding: 5,
    margin: 5,
    backgroundColor: UIColorPalette.logItemBackground,
    color: UIColorPalette.logItemTextColor,
    borderLeft: 'solid 2px rgba(0, 0, 0, 0)',
    fontSize: 14,
  }),
  entry: css({
    marginBottom: 2,
    '&:last-child': {
      marginBottom: 0,
    },
  }),
  symbolContainer: css({
    marginLeft: 1,
    alignSelf: 'center',
  }),
  symbol: css({
    width: 15,
    height: 'auto',
  }),
  contentContainer: css({
    marginLeft: 10,
    maxWidth: '90%',
    paddingRight: 10,
    wordWrap: 'break-word',
  }),
  contentItemWrapper: css({
    padding: 0,
    margin: 0,
  }),

  divider: css({
    maxHeight: 8,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    backgroundColor: UIColorPalette.logDividerBackground,
    fontSize: 13,
    textAlign: 'center',
    display: 'block',
  }),
  dividerContent: css({
    paddingLeft: 5,
    paddingRight: 5,
    marginTop: -3,
  }),
};

function renderCounterDelta(
  counters: Partial<CounterDelta>,
  forceResource?: Resource,
): React.ReactNode {
  return Resources.map((resource: Resource) => {
    if (
      resource === forceResource ||
      (counters[resource] && counters[resource] > 0)
    ) {
      return (
        <span key={resource} css={CounterDeltaStyles.container}>
          {counters[resource] || 0}
          <SymbolView
            symbol={ResourceToSymbol[resource]}
            css={CounterDeltaStyles.symbol}
          />
        </span>
      );
    }
    return null;
  });
}
const CounterDeltaStyles = {
  container: css({
    marginLeft: 2,
  }),
  symbol: css({
    height: 12,
    width: 12,
    marginLeft: 1,
  }),
};

function UserName(props: {
  className?: string;
  style?: React.CSSProperties;
  user: UserWithColor;
}) {
  return (
    <span
      css={UserNameStyle}
      className={props.className}
      style={{
        ...props.style,
        color: props.user.color,
      }}
    >
      {props.user.name}
    </span>
  );
}
const UserNameStyle = css({
  fontWeight: 'bold',
});

function CardsNameView<T extends { id: string; name: string }>(props: {
  cards: T[];
  renderer: HoverCardRenderer;
}) {
  let card_name_views: React.ReactNode[] = [];
  props.cards.forEach((card, i) => {
    if (i !== 0) {
      card_name_views.push(', ');
    }
    card_name_views.push(
      <CardNameView key={i} card={card} renderer={props.renderer} />,
    );
  });
  return <>{card_name_views}</>;
}

function TriggeredCardsView(props: {
  triggeredCards?: TriggeredCardInfo[];
  cardByID: { [k: string]: CardWithID };
}) {
  const { triggeredCards, cardByID } = props;
  if (!triggeredCards || triggeredCards.length === 0) {
    return null;
  }
  const totalDelta = sumCounters(triggeredCards.map((card) => card.counters));
  return (
    (props.triggeredCards && props.triggeredCards.length > 0 && (
      <>
        {' '}
        and triggered{' '}
        <CardsNameView
          cards={triggeredCards.map((card) => cardByID[card.cardID])}
          renderer={BasicCardRenderer}
        />{' '}
        for {renderCounterDelta(totalDelta)}
      </>
    )) ||
    null
  );
}

export function ConflictResultsView(props: {
  event: GameEventOfType<EventTypes.CONFLICT_RESULTS>;
  age: number;
  userByID: Map<string, UserWithColor>;
  cardByID: { [k: string]: CardWithID };
}) {
  const { event, age, userByID, cardByID } = props;
  const rounds = [...event.payload.rounds];
  const card = cardByID[event.payload.cardID];
  const firstRound = rounds[0];
  let playerIDToWinPercentage = mapValues(firstRound, (result, player_id) => {
    let self_bonus = result.bonus;

    const diceCount = result.rolls.length;
    const opponent_dice_bonus_tuples = Object.entries(firstRound)
      .filter(([player_id2, result]) => player_id2 !== player_id)
      .map<[number, number]>(([player_id2, result]) => {
        return [diceCount, result.bonus];
      });

    let raw_percent = Probability.probabilityWinningMultiBattle(
      diceCount,
      self_bonus,
      opponent_dice_bonus_tuples,
    );
    return Math.floor(Math.round(raw_percent * 100000) / 1000);
  });

  rounds.reverse();
  const roundViews = rounds.map((results_by_player_id, round_i) => {
    let results = Object.values(results_by_player_id);
    results.sort((a, b) => b.total - a.total);

    const playerRows = results.map((result, player_i) => {
      const user = userByID.get(result.playerID)!;
      const rendered_rolls = result.rolls.map((roll, round_i) => (
        <DieView roll={roll} key={`roll${round_i}`} />
      ));
      const is_winner =
        round_i === 0 && result.playerID === event.payload.winningPlayerID;
      const show_percent =
        is_winner || (round_i === rounds.length - 1 && results.length > 1);
      const win_percent = show_percent
        ? ` (${playerIDToWinPercentage[result.playerID]}%)`
        : '';
      return (
        <div key={player_i} css={ConflictResultsStyle.playerRow}>
          <div>
            {is_winner ? '\u2605' : ''}
            <UserName user={user} />:
          </div>
          <div>
            {rendered_rolls} + {result.bonus} = {result.total}
          </div>
          <div>{win_percent}</div>
        </div>
      );
    });
    return (
      <div key={round_i} css={ConflictResultsStyle.rollRound}>
        {playerRows}
      </div>
    );
  });

  return (
    <>
      <div css={ChatLogItemStyles.entry}>
        Clash results for{' '}
        <CardNameView card={card} renderer={BasicCardRenderer} />
      </div>
      {roundViews}
    </>
  );
}
const ConflictResultsStyle = {
  rollRound: css({
    '&:not(:last-child)': {
      '&::after': {
        display: 'block',
        content: '""',
        margin: 5,
        marginLeft: 10,
        marginRight: 15,
        borderBottom: `1px solid ${UIColorPalette.conflictResultsRoundDivider}`,
        height: 1,
        width: '100%',
      },
    },
  }),
  playerRow: css({
    display: 'grid',
    gridTemplateColumns: '102px 105px 44px',
    marginBottom: 2,
    '&:last-child': {
      marginBottom: 0,
    },
  }),
};
